import React, {useState} from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {POLYMER_SUBTYPE_TO_MODEL_TYPE, PolymerSubtype} from 'constants/MolbioConstants';
import {getLocationInfoErrors} from 'molbio/bulk-assembly/assemble-modal/FinalizationUtil';
import {SaveConstructsStep} from 'molbio/bulk-assembly/assemble-modal/SaveConstructsStep';
import {FinalizationLocationInfo} from 'molbio/bulk-assembly/BulkAssemblyDAO';
import {DEFAULT_SAVE_INFORMATION, LocationInformation} from 'molbio/components/SaveLocationPicker';
import Modal, {ModalButton} from 'ui-library/modals/Modal';

/**
 * Get the ModalButtons to display in the modal footer.
 * @param locationInfo Information about where to save the constructs.
 * @param onClickFinalize A function to be called when the modal is submitted.
 */
function getStepButtons({
  locationInfo,
  onClickFinalize,
}: {
  locationInfo: LocationInformation;
  onClickFinalize: () => void;
}): Array<ModalButton> {
  const errors = getLocationInfoErrors({locationInfo, displayString: 'constructs'});
  const disableNextButton = errors.length > 0;
  return [
    {
      text: 'Finalize',
      submit: true,
      disabled: disableNextButton,
      tooltipTitle: disableNextButton ? errors.join(' ') : undefined,
      onClick: onClickFinalize,
    },
  ];
}

export interface PerformConcatenationModalProps {
  initialFolderId: string | null;
  onRequestClose: () => void;
  onRequestSubmit: (saveInformation: FinalizationLocationInfo) => void;
  currentUserId: string;
  outputConstructSubtype: PolymerSubtype;
  numConstructs: number;
}

/**
 * A modal used to select the location in which the assembly constructs should be saved. Upon submission, the
 * assembly is completed on the backend.
 *
 * @param {PerformConcatenationModalProps} props Component props
 * @param {Function} props.onRequestClose Handler for closing the modal without submitting
 * @param {Function} props.onRequestSubmit Handler for submitting the modal
 * @param {string} props.currentUserId The API identifier of the current user, which is used for displaying
 *                                     the create worklist UI
 * @param {string} props.outputConstructSubtype The supported output type for the constructs
 */
export default function PerformConcatenationModal(props: PerformConcatenationModalProps): JSX.Element {
  const [constructLocationInfo, setConstructLocationInfo] = useState<LocationInformation>({
    ...DEFAULT_SAVE_INFORMATION,
    targetFolderId: props.initialFolderId ?? undefined,
    tagSchemaId: null,
  });

  const onClickFinalize = (): void => {
    assert(
      constructLocationInfo.targetFolderId != null,
      'Expected construct folder to be specified when saving those entities'
    );
    props.onRequestSubmit({
      fragmentSaveInfo: null,
      constructSaveInfo: {
        folderId: constructLocationInfo.targetFolderId,
        tagSchemaId: constructLocationInfo.tagSchemaId,
        worklistOptions: constructLocationInfo.worklistParams?.newWorklistInfo?.name
          ? {
              worklistDestination: 'new',
              newWorklistName: constructLocationInfo.worklistParams.newWorklistInfo.name,
            }
          : constructLocationInfo.worklistParams?.targetWorklistId
            ? {
                worklistDestination: 'existing',
                worklistId: constructLocationInfo.worklistParams.targetWorklistId,
              }
            : undefined,
      },
      primerSaveInfo: null,
    });
  };

  const buttons = getStepButtons({
    locationInfo: constructLocationInfo,
    onClickFinalize,
  });

  return (
    <Modal title="Finalize assembly" buttons={buttons} onRequestClose={props.onRequestClose}>
      <p>Add constructs to a folder</p>
      <SaveConstructsStep
        locationInfo={constructLocationInfo}
        onUpdateLocationInfo={setConstructLocationInfo}
        worklistParams={{
          useWorklistCheckboxLabel: 'Add constructs to a worklist',
          newWorklistOwnerId: props.currentUserId,
          pluralEntityLabel: 'constructs',
          shouldShowOverflowWarningForNewWorklist:
            props.numConstructs > BenchlingConfig.WORKLISTS_MAX_ITEMS_PER_WORKLIST,
          shouldShowOverflowWarningForExistingWorklist: props.numConstructs > 100,
        }}
        isRequiredParams={{isRequired: true}}
        oligoAwareFolderItemTypes={[POLYMER_SUBTYPE_TO_MODEL_TYPE[props.outputConstructSubtype]]}
      />
    </Modal>
  );
}
